import type {
  GeneratedFormDefinition,
  GeneratedFormField,
} from '@template-ui/helpers/generated-form.helpers';
import type { TerminologyFormControlUsageMap } from './terminology-form-identity.directive';
import type { TerminologyUsageDeclaration } from './terminology-usage';
import { createTerminologyUsageSignals } from './terminology-usage-signals';

export interface RegisteredGeneratedFieldTerminology {
  readonly name: string;
  readonly usage: TerminologyUsageDeclaration;
  readonly options?: readonly {
    readonly value: string;
    readonly usage: TerminologyUsageDeclaration;
  }[];
}
export interface RegisteredGeneratedFormTerminology {
  /** A code-installed generated contract, independently of the runtime/configured form. */
  readonly definition: GeneratedFormDefinition;
  readonly fields: readonly RegisteredGeneratedFieldTerminology[];
}
export interface GeneratedFormTerminologyDiagnostic {
  readonly reasonCode:
    | 'terminology_form_literal'
    | 'terminology_form_schema_unregistered'
    | 'terminology_form_label_unregistered'
    | 'terminology_form_label_mismatch';
  /** Only installed usage identity is retained; authored labels/unknown tokens/values are excluded. */
  readonly usageId?: string;
}
export interface GeneratedFormTerminologyProjection {
  readonly definition: GeneratedFormDefinition;
  readonly controls: TerminologyFormControlUsageMap;
  readonly diagnostics: readonly GeneratedFormTerminologyDiagnostic[];
}

/**
 * Adapts installed usage declarations to generated labels. This delegates wording to the existing
 * published-map helper; runtime schema tokens never create usages or select a configuration.
 */
export function createRegisteredGeneratedFormTerminology(
  registrations: readonly RegisteredGeneratedFormTerminology[],
) {
  const usages = registrations.flatMap((form) =>
    form.fields.flatMap((field) => [
      field.usage,
      ...(field.options?.map((option) => option.usage) ?? []),
    ]),
  );
  if (new Set(usages.map((usage) => usage.usageId)).size !== usages.length)
    throw new Error(
      'Generated terminology registrations require unique usage identities.',
    );
  if (
    new Set(
      registrations.map(
        (form) => `${form.definition.contract}:${form.definition.version}`,
      ),
    ).size !== registrations.length
  )
    throw new Error(
      'Generated terminology registrations require unique installed contracts.',
    );
  registrations.forEach((form) =>
    form.fields.forEach((field) => {
      const installed = form.definition.fields.find(
        (item) => item.name === field.name,
      );
      if (
        !installed ||
        !registeredLabel(installed.labelToken, installed.label, field.usage)
      )
        throw new Error(
          'Generated field usage must match its installed contract token and fallback.',
        );
      field.options?.forEach((option) => {
        const installedOption = installed.options?.find(
          (item) => item.value === option.value,
        );
        if (
          !installedOption ||
          !registeredLabel(
            installedOption.labelToken,
            installedOption.label,
            option.usage,
          )
        )
          throw new Error(
            'Generated option usage must match its installed contract token and fallback.',
          );
      });
    }),
  );
  const text = createTerminologyUsageSignals(
    Object.fromEntries(usages.map((usage) => [usage.usageId, usage])),
  );
  const cache = new WeakMap<
    GeneratedFormDefinition,
    { key: string; projection: GeneratedFormTerminologyProjection }
  >();

  return {
    project(
      definition: GeneratedFormDefinition,
    ): GeneratedFormTerminologyProjection {
      const registration = registrations.find(
        (form) =>
          form.definition.contract === definition.contract &&
          form.definition.version === definition.version,
      );
      const diagnostics: GeneratedFormTerminologyDiagnostic[] = [];
      const controls: Record<string, TerminologyUsageDeclaration> = {};
      const resolve = (
        token: string,
        fallback: string,
        installed: { labelToken: string; label: string } | undefined,
        usage: TerminologyUsageDeclaration | undefined,
      ): string => {
        if (!token) {
          diagnostics.push({ reasonCode: 'terminology_form_literal' });
          return fallback;
        }
        if (!registration)
          diagnostics.push({
            reasonCode: 'terminology_form_schema_unregistered',
          });
        else if (!installed || !usage)
          diagnostics.push({
            reasonCode: 'terminology_form_label_unregistered',
          });
        else if (installed.labelToken !== token || installed.label !== fallback)
          diagnostics.push({
            reasonCode: 'terminology_form_label_mismatch',
            usageId: usage.usageId,
          });
        else return text[usage.usageId]();
        return fallback;
      };
      const fields: GeneratedFormField[] = definition.fields.map((field) => {
        const binding = registration?.fields.find(
          (item) => item.name === field.name,
        );
        const installed = registration?.definition.fields.find(
          (item) => item.name === field.name,
        );
        const label = resolve(
          field.labelToken,
          field.label,
          installed,
          binding?.usage,
        );
        if (
          binding &&
          installed &&
          field.labelToken === installed.labelToken &&
          field.label === installed.label
        )
          controls[field.name] = binding.usage;
        const options = field.options?.map((option) => {
          const bindingOption = binding?.options?.find(
            (item) => item.value === option.value,
          );
          const installedOption = installed?.options?.find(
            (item) => item.value === option.value,
          );
          const label = resolve(
            option.labelToken,
            option.label,
            installedOption,
            bindingOption?.usage,
          );
          return label === option.label ? option : { ...option, label };
        });
        return label === field.label &&
          (!options ||
            options.every((option, index) => option === field.options?.[index]))
          ? field
          : { ...field, label, ...(options ? { options } : {}) };
      });
      const key = JSON.stringify([
        definition,
        fields.map((field) => [
          field.label,
          field.options?.map((option) => option.label),
        ]),
        Object.values(controls).map((usage) => usage.usageId),
        diagnostics,
      ]);
      const previous = cache.get(definition);
      if (previous?.key === key) return previous.projection;
      const projection: GeneratedFormTerminologyProjection = {
        definition: fields.every(
          (field, index) => field === definition.fields[index],
        )
          ? definition
          : { ...definition, fields },
        controls: Object.freeze(controls),
        diagnostics: Object.freeze(diagnostics),
      };
      cache.set(definition, { key, projection });
      return projection;
    },
  };
}
function registeredLabel(
  token: string,
  fallback: string,
  usage: TerminologyUsageDeclaration,
): boolean {
  return (
    !!token &&
    token.trim().toLowerCase() === usage.token.code &&
    fallback === usage.fallbackText
  );
}
